import React from 'react'
import OfferCountdown from 'components/OfferCountdown'
import './offer-journey.css'

export interface OfferJourneyMoment {
  /** When this happens, e.g. "Today", "Day 5", "Month 4". */
  when: string
  /** What happens, written from the learner's side. */
  detail: string
  /** 'now' = filled dot (where you are), 'ahead' = ring, 'later' = muted ring. */
  marker?: 'now' | 'ahead' | 'later'
  /** Rail segment BELOW this dot: 'offer' (green — inside the free/discount window) or 'after'. */
  rail?: 'offer' | 'after'
}

export interface OfferJourneyProps {
  moments?: OfferJourneyMoment[]
  /** Headline in the card's header band. */
  title?: string
  /** Optional pill badge above the headline (omitted by default). */
  eyebrow?: string
  /** Offer deadline (unix seconds). When set and in the future, a compact countdown sits in the header. */
  expiresAt?: number
  /** Fired once when the countdown reaches zero. */
  onExpire?: () => void
}

const DEFAULT_MOMENTS: OfferJourneyMoment[] = [
  {
    when: 'Today',
    detail: 'Pick Standard or Pro and start learning. Full access, nothing charged.',
    marker: 'now',
    rail: 'offer',
  },
  {
    when: 'Day 5',
    detail: 'Your free trial ends and your plan begins — at 50% off for 3 months.',
    marker: 'ahead',
    rail: 'after',
  },
  {
    when: 'Month 4',
    detail: 'You pay the regular price. Cancel anytime before, in one click.',
    marker: 'later',
  },
]

/**
 * The welcome-offer card on the plan page (trial-offer landings): a header band
 * naming the offer and its deadline, over a vertical day-by-day timeline of
 * exactly what happens and when. The rail is green while the learner is inside
 * the free/discount window and fades to gray after it — the timeline itself
 * carries the offer's shape, so nothing else on the card needs to decorate.
 */
const OfferJourney: React.FC<OfferJourneyProps> = ({
  moments = DEFAULT_MOMENTS,
  title = '5 days free, then 50% off your first 3 months',
  eyebrow,
  expiresAt,
  onExpire,
}) => {
  const hasDeadline = typeof expiresAt === 'number' && expiresAt * 1000 > Date.now()
  return (
    <section className='offer-journey'>
      <header className='offer-journey-head'>
        <div className='offer-journey-heading'>
          {eyebrow && <span className='offer-journey-eyebrow'>{eyebrow}</span>}
          <h3 className='offer-journey-title'>{title}</h3>
        </div>
        {hasDeadline && (
          <OfferCountdown variant='inline' label='' expiresAt={expiresAt as number} onExpire={onExpire} />
        )}
      </header>
      <ol className='offer-journey-timeline'>
        {moments.map((moment, index) => (
          <li className='offer-journey-moment' key={index}>
            <span className='offer-journey-rail' aria-hidden='true'>
              <span className={`offer-journey-dot offer-journey-dot--${moment.marker || 'ahead'}`} />
              {index < moments.length - 1 && (
                <span className={`offer-journey-line offer-journey-line--${moment.rail || 'after'}`} />
              )}
            </span>
            <span className='offer-journey-when'>{moment.when}</span>
            <p className='offer-journey-detail'>{moment.detail}</p>
          </li>
        ))}
      </ol>
    </section>
  )
}

export default OfferJourney
